import type {
	ExportMessage,
	OptimizationUpdateMessage,
} from '@/shared/api/socket-messages'
import type { Socket } from '@/shared/api/websocket'
import type { Message } from '@/shared/api/websocket/types'
import type { OptimizationGroupModel } from '@/shared/generated'

import * as React from 'react'
import { prop, size, throttle, uniqBy } from 'lodash/fp'
import { toast } from 'sonner'

import { optimizationGroupQueries } from '@/entities/optimization-group'

import { Button } from '@/shared/components/ui/button'
import { OptimizationStatus } from '@/shared/generated'
import i18n from '@/shared/i18n/client'
import { queryClient } from '@/shared/lib/query-client'
import { $optimizationUpdate } from '@/shared/lib/socket'
import SocketProvider from '@/shared/lib/socket/provider'
import logger from '@/shared/tools/logger'

type NotifyMessage = {
	msgBody: any
	optimizationGroup?: OptimizationGroupModel
	showButton?: boolean
	onClick?: () => void
}
type AddMessageProps = {
	msgBody: Message
	optimizationGroup?: OptimizationGroupModel
	showButton?: boolean
	onClick?: () => void
}

export class SocketMessagesNotification {
	private _messages: NotifyMessage[] = []
	private _active = true
	public addMessage({
		msgBody,
		optimizationGroup,
		showButton = false,
		onClick,
	}: AddMessageProps) {
		if (!this._active) return
		this._messages.push({ msgBody, optimizationGroup, showButton, onClick })
		this.showMessages()
	}
	public subscribe() {
		this._active = true
	}
	public unsubscribe() {
		this._active = false
	}
	private showMessages = throttle(5000, () => {
		const t = i18n.t
		if (!this._active) return
		if (this._messages.length === 0) return
		if (
			this._messages.length === 1 &&
			this._messages[0].optimizationGroup?.name
		) {
			const { msgBody, optimizationGroup } = this._messages[0]
			const statusText = t(`opt_${msgBody.payload.optimization_status}`, {
				defaultValue: msgBody.payload.optimization_status,
			})
			const content = {
				title: t('notification.og_status_changed.title', {
					name: optimizationGroup?.name,
					defaultValue: `Optimization group "${optimizationGroup?.name}"`,
				}),
				description: t('notification.og_status_changed.body', {
					status: statusText,
					defaultValue: `status was changed to "${statusText}".`,
				}),
			}
			if (msgBody.payload.optimization_status === 'finished') {
				toast.success(content.title, {
					description: content.description,
					id: msgBody.payload.optimization_group_id,
				})
			} else {
				toast.info(content.title, {
					description: content.description,
					id: msgBody.payload.optimization_group_id,
				})
			}
		} else {
			const count = size(
				uniqBy(prop('msgBody.payload.optimization_group_id'), this._messages)
			)
			const showRefreshButton =
				this._messages[0].showButton && this._messages[0].onClick
			const handleClick = this._messages[0]?.onClick
			const toastId = 'common'

			toast.info(
				t('notification.count_of_og_updated.title', {
					count,
					defaultValue: `${count} Optimization groups were updated.`,
				}),
				{
					id: toastId,
					action: !!showRefreshButton && (
						<Button
							size='sm'
							onClick={() => {
								handleClick?.()
								toast.dismiss(toastId)
							}}
						>
							{t('general_refresh', { defaultValue: 'Refresh' })}
						</Button>
					),
					duration: showRefreshButton ? Infinity : 0,
				}
			)
		}
		this._messages = []
	})
}

export const socketMessagesNotification = new SocketMessagesNotification()

const resetCommonQueries = throttle(1000, () => {
	queryClient.invalidateQueries({ queryKey: ['optimizations'] })
	queryClient.invalidateQueries({ queryKey: ['pricing-alerts'] })
	queryClient.invalidateQueries({ queryKey: ['products-counts-by-pc'] })
})

const resetOptimizationGroupQueries = throttle(3000, () => {
	queryClient.invalidateQueries({
		queryKey: ['scenarios'],
	})
	queryClient.invalidateQueries({
		queryKey: ['interpretation'],
	})
})

/**
 * Handle export update event
 * @param msg - Message instance
 */
const handleExportUpdate = (msg: Message<ExportMessage>) => {
	const t = i18n.t
	queryClient.invalidateQueries({ queryKey: ['exports'] })
	if (msg.payload.status === 'started') {
		toast.loading(
			t('export.started.body', {
				name: msg.payload.name,
				defaultValue: `Export "${msg.payload.name}" was started`,
			}),
			{ id: msg.payload.id }
		)
	} else if (msg.payload.status === 'finished') {
		toast.success(
			t('export.finished.body', {
				name: msg.payload.name,
				defaultValue: `Export "${msg.payload.name}" was finished`,
			}),
			{ id: msg.payload.id, duration: 5000 }
		)
		localStorage.setItem('new_exports_unreaded', JSON.stringify(true))
		window.dispatchEvent(
			new StorageEvent('local-storage', { key: 'new_exports_unreaded' })
		)
	}
}

/**
 * Handle optimization update event
 * @param msg - Message instance
 */
const handleOptimizationUpdate = async (
	msg: Message<OptimizationUpdateMessage>
) => {
	// Check if optimization update is dry run (unimportant)
	if (msg.payload.run_type === 'dry_run') {
		// Skip dry run optimization updates
		return
	}
	logger.debug('WS', 'optimization:update', msg)
	const optimizationGroupId = msg.payload.optimization_group_id
	// Fire event for optimization group id
	$optimizationUpdate.fireEvent(
		`optimization:${optimizationGroupId}:update`,
		msg
	)

	// Get optimization group name
	const optimizationGroup = await queryClient.fetchQuery(
		optimizationGroupQueries.single(optimizationGroupId)
	)
	// Reset all releated queries
	queryClient.invalidateQueries({
		queryKey: ['optimization', optimizationGroupId],
	})

	if (
		![OptimizationStatus.Updating, OptimizationStatus.Pending].includes(
			msg.payload.optimization_status as OptimizationStatus
		)
	) {
		queryClient.invalidateQueries({
			queryKey: ['main-scenario', optimizationGroupId],
		})
		resetOptimizationGroupQueries()
	}

	resetCommonQueries()

	// Show notification about optimization status change for optimization group
	socketMessagesNotification.addMessage({
		msgBody: msg,
		optimizationGroup,
	})
}

/**
 * Handle socket connection
 * @param socket - Socket instance
 */
const onConnect = async (socket: Socket) => {
	const optimizationChannel = socket.channel('optimization')
	const exportsChannel = socket.channel('export')
	// Subscribe to exports channel to receive updates
	exportsChannel.subscribe()
	// Subscribe to optimization channel to receive updates
	optimizationChannel.subscribe()
	// Handle export update event
	exportsChannel.on('update', handleExportUpdate)
	// Handle optimization update event
	optimizationChannel.on('update', handleOptimizationUpdate)
}

export default function SocketProvidere({
	children,
}: {
	children: React.ReactNode
}) {
	return (
		<SocketProvider
			onConnect={onConnect}
			notifications={socketMessagesNotification}
		>
			{children}
		</SocketProvider>
	)
}
